import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Check,
  X,
  ZoomIn,
  ImageOff,
  ChevronLeft,
  ChevronRight,
  CheckCheck,
  Reply,
} from "lucide-react"
import Avatar from "./Avatar"
import { renderMarkdown, OfferCard } from "../AiChatBot/AiChatParts"
import { useTranslation } from "react-i18next"
import { resolveDepartmentColorHex } from "../../utils/variables/departaments"
import { READ_MESSAGE_STATUS } from "../../utils/variables/chat"
import { getDepartmentColorScheme } from "../utils/color/department_colors"
import { META_CHANNELS, CHANNEL_LABELS } from "./constants"

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const AVATAR_COLORS = [
  "#7C3AED",
  "#0891B2",
  "#059669",
  "#D97706",
  "#DC2626",
  "#2563EB",
  "#DB2777",
  "#65A30D",
]

const getColorForName = (name = "") => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

// Принимает дату прочтения (поле `at` из read_log) и форматирует в дд.мм чч:мм
const formatReadAt = (at) => {
  if (!at) return null
  try {
    return new Date(at).toLocaleString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
    })
  } catch {
    return null
  }
}

const ImageLightbox = ({ images, initialIndex = 0, onClose }) => {
  const [current, setCurrent] = useState(initialIndex)

  const prev = useCallback(() => {
    setCurrent((i) => (i - 1 + images.length) % images.length)
  }, [images.length])

  const next = useCallback(() => {
    setCurrent((i) => (i + 1) % images.length)
  }, [images.length])

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft") prev()
      if (e.key === "ArrowRight") next()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose, prev, next])

  const file = images[current]

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {images.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/40 text-white text-sm font-medium select-none z-10">
            {current + 1} / {images.length}
          </div>
        )}

        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              prev()
            }}
            className="absolute left-3 sm:left-6 p-2 rounded-full bg-white/10 hover:bg-white/25 transition-colors z-10"
          >
            <ChevronLeft className="w-7 h-7 text-white" />
          </button>
        )}

        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            initial={{ opacity: 0, scale: 0.93, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.93, x: -30 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            src={file.url}
            alt={file.file_name || "image"}
            className="max-w-[85vw] max-h-[85vh] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </AnimatePresence>

        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              next()
            }}
            className="absolute right-3 sm:right-6 p-2 rounded-full bg-white/10 hover:bg-white/25 transition-colors z-10"
          >
            <ChevronRight className="w-7 h-7 text-white" />
          </button>
        )}

        {images.length > 1 && (
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-2 rounded-2xl bg-black/40 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                  i === current
                    ? "border-white scale-110 shadow-lg"
                    : "border-transparent opacity-60 hover:opacity-90"
                }`}
              >
                <img
                  src={img.url}
                  alt={img.file_name || ""}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

const BROWSER_IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"]
const BROWSER_IMAGE_MIME = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
  "image/avif",
]

const isImage = (file) => {
  if (file.content_type) return BROWSER_IMAGE_MIME.includes(file.content_type)
  if (file.file_name) {
    const ext = file.file_name.split(".").pop()?.toLowerCase()
    return BROWSER_IMAGE_EXTS.includes(ext)
  }
  return false
}

const ImagePreview = ({ file, onClick }) => {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const hasUrl = Boolean(file.url)

  if (!hasUrl) {
    return (
      <div className="w-full h-32 rounded-xl bg-slate-100 border border-slate-200 flex flex-col items-center justify-center gap-1.5">
        <ImageOff className="w-6 h-6 text-slate-300" />
        {file.file_name && (
          <p className="text-[11px] text-slate-400 truncate max-w-[80%]">{file.file_name}</p>
        )}
        <p className="text-[10px] text-slate-300">{t("admin.loading_dots")}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-32 rounded-xl bg-red-50 border border-red-100 flex flex-col items-center justify-center gap-1.5">
        <ImageOff className="w-6 h-6 text-red-300" />
        {file.file_name && (
          <p className="text-[11px] text-red-400 truncate max-w-[80%]">{file.file_name}</p>
        )}
      </div>
    )
  }

  return (
    <div
      className="relative group cursor-zoom-in rounded-xl overflow-hidden bg-slate-100 h-full"
      onClick={onClick}
    >
      {!loaded && <div className="w-full h-40 animate-pulse bg-slate-200 rounded-xl" />}
      <img
        src={file.url}
        alt={file.file_name || "image"}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={`w-full max-h-72 object-cover rounded-xl transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0 absolute inset-0"}`}
      />
      {loaded && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
        </div>
      )}
      {loaded && file.file_name && (
        <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-xs text-white truncate">{file.file_name}</p>
        </div>
      )}
    </div>
  )
}

const ImageGrid = ({ images, onImageClick }) => {
  const count = images.length

  if (count === 1) {
    return (
      <div className="mx-3 mb-2">
        <ImagePreview file={images[0]} onClick={() => onImageClick(0)} />
      </div>
    )
  }

  if (count === 2) {
    return (
      <div className="mx-3 mb-2 grid grid-cols-2 gap-1">
        {images.map((img, i) => (
          <ImagePreview key={i} file={img} onClick={() => onImageClick(i)} />
        ))}
      </div>
    )
  }

  if (count === 3) {
    return (
      <div className="mx-3 mb-2 grid grid-cols-2 gap-1">
        <div className="row-span-2">
          <ImagePreview file={images[0]} onClick={() => onImageClick(0)} />
        </div>
        <ImagePreview file={images[1]} onClick={() => onImageClick(1)} />
        <ImagePreview file={images[2]} onClick={() => onImageClick(2)} />
      </div>
    )
  }

  const visible = images.slice(0, 4)
  const hiddenCount = count - 4

  return (
    <div className="mx-3 mb-2 grid grid-cols-2 gap-1">
      {visible.map((img, i) => (
        <div key={i} className="relative">
          <ImagePreview file={img} onClick={() => onImageClick(i)} />
          {i === 3 && hiddenCount > 0 && (
            <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center pointer-events-none">
              <span className="text-white text-2xl font-bold">+{hiddenCount}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

const DeptSelector = ({ msg, departments, onChangeDept, onCancelEdit, c }) => {
  const [status, setStatus] = useState(null)
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const handleChange = async (e) => {
    const newId = Number(e.target.value)
    if (newId === msg.department_id) return
    setStatus("loading")
    try {
      await onChangeDept(msg.id, newId)
      setStatus("success")
      timerRef.current = setTimeout(() => setStatus(null), 1800)
    } catch {
      setStatus("error")
      timerRef.current = setTimeout(() => setStatus(null), 2200)
    }
  }

  const isNeutralStatus = status === "loading" || status === "error"
  const ringClass =
    status === "success"
      ? "border-emerald-400 ring-2 ring-emerald-200"
      : status === "error"
        ? "border-red-400 ring-2 ring-red-200"
        : "border"

  return (
    <div className="relative flex items-center gap-1.5">
      <div
        className={`relative flex items-center rounded-full transition-all duration-200 ${ringClass}`}
        style={!isNeutralStatus ? { borderColor: c.border, background: c.selectBg } : undefined}
      >
        <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none w-3 h-3 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {status === "loading" && (
              <motion.div
                key="spin"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={{ duration: 0.12 }}
                className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: c.spinnerBorder, borderTopColor: "transparent" }}
              />
            )}
            {status === "success" && (
              <motion.div
                key="ok"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <CheckCheck className="w-3 h-3 text-emerald-500" />
              </motion.div>
            )}
            {status === "error" && (
              <motion.div
                key="err"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.12 }}
              >
                <X className="w-3 h-3 text-red-500" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <select
          value={msg.department_id}
          onChange={handleChange}
          disabled={status === "loading"}
          className="pl-7 pr-7 py-0.5 bg-transparent rounded-full text-xs font-semibold uppercase tracking-wide appearance-none cursor-pointer focus:outline-none disabled:cursor-wait transition-colors"
          style={{ color: c.text }}
        >
          {departments?.map((d) => (
            <option key={d.id} value={d.id} disabled={d.id === msg.department_id}>
              {d.name}
            </option>
          ))}
        </select>
        <svg
          className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none"
          style={{ color: c.arrow }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      <button
        onClick={() => onCancelEdit(msg.id)}
        className="p-0.5 hover:bg-slate-200 rounded-full transition-colors"
        title="Скасувати"
      >
        <X className="w-3.5 h-3.5 text-slate-500" />
      </button>
    </div>
  )
}

const ReadByRow = ({ readers = [], isLegacy = false, t }) => {
  const [expanded, setExpanded] = useState(false)

  if (readers.length === 0) return null

  // Бэкенд отдаёт дату прочтения в поле `at` (см. read_log.history), не `read_at`
  const allUsers = readers.reduce((acc, r) => {
    const existing = acc.findIndex((u) => u.user_id === r.user_id)
    if (existing === -1) {
      acc.push(r)
    } else {
      if (new Date(r.at) > new Date(acc[existing].at)) {
        acc[existing] = r
      }
    }
    return acc
  }, [])

  const currentReaders = allUsers.filter((r) => !r.action || r.action === "read")

  const MAX_VISIBLE = 3
  const visible = allUsers.slice(0, MAX_VISIBLE)
  const overflow = allUsers.length - MAX_VISIBLE

  return (
    <div className="border-t border-slate-100">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-1.5 px-4 pb-2 pt-1.5 hover:bg-slate-50 transition-colors text-left"
      >
        <svg
          className="w-3 h-3 text-slate-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>

        <div className="flex items-center flex-shrink-0">
          {visible.map((r, i) => {
            const color = getColorForName(r.name)
            const initials = getInitials(r.name)
            const readAtLabel = formatReadAt(r.at)
            const isCurrentlyRead = !r.action || r.action === "read"
            return (
              <div
                key={r.user_id ?? i}
                title={readAtLabel ? `${r.name} · ${readAtLabel}` : r.name}
                className="w-5 h-5 rounded-full border-[1.5px] border-white flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0 cursor-default select-none transition-opacity"
                style={{
                  background: color,
                  marginLeft: i === 0 ? 0 : -6,
                  zIndex: visible.length - i,
                  position: "relative",
                  opacity: isCurrentlyRead ? 1 : 0.35,
                }}
              >
                {initials}
              </div>
            )
          })}
          {overflow > 0 && (
            <div
              className="w-5 h-5 rounded-full border-[1.5px] border-white flex items-center justify-center text-[9px] font-semibold text-slate-500 bg-slate-200 flex-shrink-0"
              style={{ marginLeft: -6, position: "relative", zIndex: 0 }}
            >
              +{overflow}
            </div>
          )}
        </div>

        <span className="text-[10px] text-slate-400 truncate flex-1">
          {currentReaders.length > 0
            ? currentReaders.map((r) => (r.name || "").split(" ")[0]).join(", ")
            : allUsers.map((r) => (r.name || "").split(" ")[0]).join(", ")}
        </span>

        <svg
          className={`w-3 h-3 text-slate-400 flex-shrink-0 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-2.5 flex flex-col gap-1">
              {readers.map((r, i) => {
                const color = getColorForName(r.name)
                const initials = getInitials(r.name)
                const readAtLabel = formatReadAt(r.at)
                const isRead = !r.action || r.action === "read"
                return (
                  <div key={i} className="flex items-center gap-2 py-0.5">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0 select-none"
                      style={{ background: color, opacity: isRead ? 1 : 0.5 }}
                    >
                      {initials}
                    </div>
                    <span className="text-[11px] text-slate-600 flex-1 truncate">{r.name}</span>
                    {readAtLabel && (
                      <span className="text-[10px] text-slate-400 flex-shrink-0">
                        {readAtLabel}
                      </span>
                    )}
                    <div
                      className={`flex items-center gap-1 flex-shrink-0 ${isRead ? "text-emerald-500" : "text-slate-400"}`}
                    >
                      {isRead ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      <span className="text-[10px] font-medium">
                        {isRead ? t("admin.read_message") : t("admin.not_read_message")}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Compact quoted preview inside a bubble (shows the message being replied to)
const ReplyPreview = ({ replyTo, c }) => {
  if (!replyTo) return null

  const plainText = replyTo.text
    ? replyTo.text
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : ""
  const previewText = plainText.length > 120 ? plainText.slice(0, 120) + "…" : plainText
  const hasImage = replyTo.files?.some(isImage)

  return (
    <div
      className="mx-3 mt-2.5 mb-0 rounded-xl overflow-hidden border-l-[3px] bg-slate-50"
      style={{ borderLeftColor: c?.borderColor ?? "#60A5FA" }}
    >
      <div className="flex items-start gap-2 px-3 py-2">
        <div className="flex-1 min-w-0">
          <p
            className="text-[11px] font-semibold truncate"
            style={{ color: c?.senderText ?? "#2563EB" }}
          >
            {replyTo.sender || "—"}
          </p>
          {previewText ? (
            <p className="text-[12px] text-slate-500 leading-snug line-clamp-2 mt-0.5">
              {previewText}
            </p>
          ) : hasImage ? (
            <p className="text-[12px] text-slate-400 italic mt-0.5">📎 Изображение</p>
          ) : (
            <p className="text-[12px] text-slate-400 italic mt-0.5">Сообщение</p>
          )}
        </div>
        {hasImage && replyTo.files?.find(isImage)?.url && (
          <img
            src={replyTo.files.find(isImage).url}
            alt=""
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-slate-200"
          />
        )}
      </div>
    </div>
  )
}

const MessageBubble = ({
  msg,
  clientChannel = "telegram",
  onToggleEdit,
  onSaveEdit,
  onCancelEdit,
  onChangeDept,
  toggleReadStatus,
  departments = [],
  handleRead,
  isAdmin,
  onReply,
  allMessages,
}) => {
  const { t } = useTranslation()
  const aiOffers = Array.isArray(msg.ai_meta?.payload)
    ? msg.ai_meta.payload.filter((offer) => offer?.book_url)
    : []

  const { text, department } = msg

  const [editText, setEditText] = useState(msg.editDraft ?? text)
  const textareaRef = useRef(null)
  const bubbleRef = useRef(null)

  const deptIndex = departments.findIndex((dept) => dept.name === department)
  const deptObj = deptIndex !== -1 ? departments[deptIndex] : null

  const c = useMemo(
    () => getDepartmentColorScheme(resolveDepartmentColorHex(deptObj, deptIndex)),
    [deptObj?.color, deptIndex]
  )

  const [lightboxIndex, setLightboxIndex] = useState(null)

  // Find the message this reply is responding to
  const replyToMsg = msg.reply_to_id ? allMessages?.find((m) => m.id === msg.reply_to_id) : null

  useEffect(() => {
    setEditText(msg.editDraft ?? msg.text)
  }, [msg.editDraft, msg.text])

  useEffect(() => {
    if (msg.isEditing && textareaRef.current) {
      textareaRef.current.focus()
      const len = textareaRef.current.value.length
      textareaRef.current.setSelectionRange(len, len)
    }
  }, [msg.isEditing])

  const images = msg.files?.filter(isImage) ?? []
  const otherFiles = msg.files?.filter((f) => !isImage(f)) ?? []
  const lightboxImages = images.filter((f) => Boolean(f.url))

  const isAlreadyRead = msg.status === READ_MESSAGE_STATUS
  const canToggle = !msg.isOwn && (isAdmin || !isAlreadyRead)

  const readers = msg.read_by ?? []

  return (
    <motion.div
      ref={bubbleRef}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className={`flex gap-2.5 ${msg.isOwn ? "justify-end" : "justify-start"}`}
    >
      {!msg.isOwn && (
        <Avatar
          className="w-9 h-9 mt-5"
          company={msg.is_ai ? "AI" : msg.company}
          src={msg.avatar}
        />
      )}

      <div className={`flex flex-col max-w-[72%] ${msg.isOwn ? "items-end" : "items-start"}`}>
        <div className="flex flex-wrap items-center gap-1 mb-0.5 px-1">
          <span className="text-xs font-semibold" style={{ color: c.senderText }}>
            {msg.sender}
          </span>
          {msg.company && (
            <>
              <span className="text-xs text-slate-400">|</span>
              <span className="text-xs font-semibold text-slate-700">{msg.company}</span>
            </>
          )}
        </div>

        {msg.department && (
          <div className="flex items-center gap-1.5 mb-1.5 px-1">
            {msg.isEditing ? (
              <DeptSelector
                msg={msg}
                departments={departments}
                onChangeDept={onChangeDept}
                onCancelEdit={onCancelEdit}
                c={c}
              />
            ) : (
              <>
                <div className="px-2.5 py-0.5 rounded-full" style={{ background: c.bg }}>
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{ color: c.text }}
                  >
                    {msg.department}
                  </span>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => onToggleEdit(msg.id)}
                    className="p-0.5 hover:bg-slate-200 rounded-full transition-colors"
                    title="Редагувати"
                  >
                    <svg
                      className="w-3.5 h-3.5 text-slate-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Bubble */}
        <div className="relative w-full">
          <div
            className={`border shadow-sm overflow-hidden w-full ${
              msg.for_admin || msg.is_hidden
                ? "bg-rose-50/40 border-rose-200"
                : msg.from_partner
                  ? "bg-amber-50/60 border-amber-300"
                  : msg.is_system
                    ? "bg-slate-50 border-slate-200"
                    : msg.isOwn
                      ? "bg-orange-50/30 border-orange-200"
                      : msg.department
            }`}
            style={{ borderRadius: msg.isOwn ? "18px 4px 18px 18px" : "4px 18px 18px 18px" }}
          >
            {msg.department && <div className="h-1 w-full" style={{ background: c.badgeSolid }} />}

            {(msg.for_admin || msg.is_hidden) && (
              <div className="flex items-center gap-1.5 px-4 pt-2.5">
                <svg
                  className="w-3 h-3 text-rose-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                </svg>
                <span className="text-[11px] font-semibold text-rose-500 uppercase tracking-wide">
                  {t("admin.for_admin")}
                </span>
              </div>
            )}

            {(msg.from_client || msg.to_client) && (
              <div className="flex items-center gap-1.5 px-4 pt-2.5">
                <svg
                  className="w-3 h-3 text-sky-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
                </svg>
                <span className="text-[11px] font-semibold text-sky-500 uppercase tracking-wide">
                  {META_CHANNELS.includes(clientChannel)
                    ? msg.from_client
                      ? t(
                          `admin.from_client_${clientChannel}`,
                          `Клиент · ${CHANNEL_LABELS[clientChannel]}`
                        )
                      : t(
                          `admin.sent_to_client_${clientChannel}`,
                          `Отправлено клиенту в ${CHANNEL_LABELS[clientChannel]}`
                        )
                    : msg.from_client
                      ? t("admin.from_client_telegram", "Клиент · Telegram")
                      : t("admin.sent_to_client_telegram", "Отправлено клиенту в Telegram")}
                </span>
              </div>
            )}

            {msg.from_partner && (
              <div className="flex items-center gap-1.5 px-4 pt-2.5">
                <svg
                  className="w-3 h-3 text-amber-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l5-8v4h4l-5 8z" />
                </svg>
                <span className="text-[11px] font-semibold text-amber-600 uppercase tracking-wide">
                  {t("admin.from_partner", "Постачальник")}
                </span>
              </div>
            )}

            {msg.is_system && !msg.from_partner && !msg.for_admin && !msg.is_hidden && (
              <div className="flex items-center gap-1.5 px-4 pt-2.5">
                <svg
                  className="w-3 h-3 text-slate-400 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t("admin.system_message", "Система")}
                </span>
              </div>
            )}

            {msg.isOwn &&
              !msg.is_ai &&
              !msg.for_admin &&
              !msg.to_client &&
              !msg.is_hidden &&
              !msg.from_partner &&
              !msg.is_system && (
                <div className="flex items-center gap-1.5 px-4 pt-2.5">
                  <svg
                    className="w-3 h-3 text-orange-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                  <span className="text-[11px] font-semibold text-orange-500 uppercase tracking-wide">
                    {t("admin.own_message", "Менеджер")}
                  </span>
                </div>
              )}

            {/* Reply-to preview block */}
            {replyToMsg && <ReplyPreview replyTo={replyToMsg} c={c} />}

            <div className="px-4 pt-3 pb-1">
              {msg.is_ai && !msg.isOwn ? (
                <p className="text-[15px] leading-[1.5] whitespace-pre-wrap break-words text-slate-900">
                  {renderMarkdown(msg.ai_content || "")}
                </p>
              ) : (
                <p
                  className="text-[15px] leading-[1.5] whitespace-pre-wrap break-words text-slate-900"
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              )}
              {aiOffers.length > 0 && (
                <div className="mt-2 mb-2 grid gap-2">
                  {aiOffers.map((offer, index) => (
                    <OfferCard key={`${offer.token}-${offer.price_id}-${index}`} offer={offer} />
                  ))}
                </div>
              )}
            </div>

            {images.length > 0 && (
              <ImageGrid images={images} onImageClick={(i) => setLightboxIndex(i)} />
            )}

            {otherFiles.length > 0 && (
              <div className="mx-3 mb-2 flex flex-col gap-1.5">
                {otherFiles.map((file, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-orange-50 overflow-hidden border border-orange-100"
                  >
                    <div className="px-4 py-2.5">
                      <p className="text-sm font-semibold text-slate-800">{file.file_name}</p>
                    </div>
                    <button
                      onClick={() => {
                        const link = document.createElement("a")
                        link.href = file.url
                        link.download = ""
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                      }}
                      disabled={!file.url}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <span className="text-sm font-semibold text-white">
                        {t("admin.download")}
                      </span>
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!msg.is_ai && (readers.length > 0 || isAlreadyRead) && (
              <ReadByRow readers={readers} isLegacy={readers.length === 0 && isAlreadyRead} t={t} />
            )}

            {/* Bottom bar: time + reply + read toggle */}
            <div className="flex items-center justify-between px-4 pb-2.5 pt-1 gap-2">
              <span className="text-[11px] text-slate-400 flex-shrink-0">{msg.time}</span>

              <div className="flex items-center gap-1.5">
                {/* Reply button — always visible if onReply provided */}
                {onReply && !msg?.is_hidden && (
                  <button
                    onClick={() => onReply(msg)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50 active:scale-95 transition-all cursor-pointer"
                    title={t("admin.reply", "Ответить")}
                  >
                    <Reply className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500" />
                    <span className="text-[11px] font-medium text-slate-500 hover:text-blue-600 whitespace-nowrap">
                      {t("admin.reply", "Ответить")}
                    </span>
                  </button>
                )}

                {/* Read toggle button */}
                {!msg.is_ai && (
                  <button
                    className={`flex items-center gap-1.5 transition-all duration-150 rounded-full px-2.5 py-1 border ${
                      !canToggle
                        ? "pointer-events-none cursor-default border-transparent opacity-60"
                        : isAlreadyRead
                          ? "border-transparent hover:border-current hover:bg-slate-50 cursor-pointer active:scale-95"
                          : "border-orange-200 bg-orange-50 hover:bg-orange-100 cursor-pointer active:scale-95"
                    }`}
                    onClick={() => {
                      if (!canToggle) return
                      toggleReadStatus()
                    }}
                  >
                    <Check
                      className={`w-3.5 h-3.5 flex-shrink-0 ${!isAlreadyRead ? "text-orange-400" : ""}`}
                      style={isAlreadyRead ? { color: c.checkRead } : undefined}
                    />
                    <span
                      className={`text-[11px] font-medium transition-colors ${!isAlreadyRead ? "text-orange-400" : ""}`}
                      style={isAlreadyRead ? { color: c.statusRead } : undefined}
                    >
                      {isAlreadyRead ? t("admin.read_message") : t("admin.not_read_message")}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={Math.min(lightboxIndex, lightboxImages.length - 1)}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </motion.div>
  )
}

export default MessageBubble
